'use client';

import { c as _c } from "react/compiler-runtime";
import { LoadingButton } from '@7f-ui/components/button/loading-button';
import { MessageBox } from '@7f-ui/components/message-box/message-box';
import { Modal } from '@7f-ui/components/modal/modal';
import { getMapUrl } from '@feat/feat-map/urls';
import { trackClickCta } from '@feat/feat-tracking/cta';
import { trackWebsiteMessage } from '@feat/feat-tracking/message';
import { DotPulse } from 'ldrs/react';
import type { AccommodationFragment } from 'src/lib/backend/documents/accommodation';
import 'ldrs/react/DotPulse.css';
import { circleToPolygon, ProductType } from '@utils/helpers';
import { shopTestId } from '@utils/utils-shop-test-ids';
import { pick } from 'es-toolkit';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import { getMinMaxLengthOfStay, type RulesOfStay, useAppAvailableDates } from '../_hooks/use-app-available-dates';
import { areDatesWithinRulesOfStay, useAppCheckAvailableRooms } from '../_hooks/use-app-check-available-rooms';
import type { AccommodationConfig } from '../_lib/accommodation-config';
import { AppAlternativeDateChips } from './app-alternative-date-chips';
const DateRangePicker = dynamic(() => import('@7f-ui/components/datepicker/date-range-picker/date-range-picker').then(module => module.DateRangePicker), {
  ssr: false,
  loading: () => <div className="grid h-full place-content-center sm:min-h-96">
        <DotPulse speed={1.3} color="currentColor" />
      </div>
});
type Accommodation = Pick<AccommodationFragment, 'id' | 'minLengthOfStay' | 'maxLengthOfStay' | 'lat' | 'lng'>;
type Message = {
  type: 'warning' | 'error' | 'info';
  message: string;
  actionLabel?: string;
} | null;
export type AppAccommodationDateModalContentProps = {
  config: AccommodationConfig;
  accommodation: Accommodation;
  isSubmitting?: boolean;
  onSubmit: (config: AccommodationConfig, fromAlternativeDates: boolean) => void;
};
type Dates = [Date | null, Date | null];
export function AppAccommodationDateModalContent(t0) {
  const $ = _c(61);
  const {
    config,
    accommodation,
    isSubmitting,
    onSubmit
  } = t0;
  let t1;
  if ($[0] !== config.from || $[1] !== config.to) {
    t1 = [config.from, config.to];
    $[0] = config.from;
    $[1] = config.to;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [dates, setDates] = useState(t1);
  const {
    id,
    maxLengthOfStay
  } = accommodation;
  let t2;
  if ($[3] !== accommodation || $[4] !== config) {
    t2 = pick({
      ...accommodation,
      ...config
    }, ["adults", "children", "allocation"]);
    $[3] = accommodation;
    $[4] = config;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const paxParams = t2;
  let t3;
  if ($[6] !== dates[0] || $[7] !== id || $[8] !== maxLengthOfStay || $[9] !== paxParams) {
    t3 = {
      id,
      paxParams,
      maxLengthOfStay,
      startDate: dates[0]
    };
    $[6] = dates[0];
    $[7] = id;
    $[8] = maxLengthOfStay;
    $[9] = paxParams;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const {
    min,
    max,
    isValid,
    rulesOfStay,
    isDateUnavailable
  } = useAppAvailableDates(t3);
  let t4;
  if ($[11] !== dates || $[12] !== rulesOfStay) {
    t4 = areDatesWithinRulesOfStay(dates, rulesOfStay);
    $[11] = dates;
    $[12] = rulesOfStay;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const enabled = t4;
  let t5;
  if ($[14] !== dates || $[15] !== enabled || $[16] !== id || $[17] !== paxParams) {
    t5 = {
      id,
      paxParams,
      dates,
      enabled
    };
    $[14] = dates;
    $[15] = enabled;
    $[16] = id;
    $[17] = paxParams;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const {
    data,
    isFetching
  } = useAppCheckAvailableRooms(t5);
  const isInvalid = !isValid(dates) || !enabled;
  const isDisabled = isFetching || isSubmitting || isInvalid || data?.rooms.length === 0;
  let t6;
  if ($[19] !== config.adults || $[20] !== config.children || $[21] !== rulesOfStay) {
    t6 = range => {
      const newDates = range ?? [null, null];
      const [from, to] = newDates;
      setDates(newDates);
      if (!from || !to) {
        return;
      }
      const {
        min: minStay,
        max: maxStay,
        difference,
        isValid: isWithinRules
      } = getMinMaxLengthOfStay(newDates, rulesOfStay);
      if (!isWithinRules) {
        trackWebsiteMessage("accommodation", {
          from,
          to,
          adults: config.adults,
          children: config.children.length,
          message: difference <= minStay ? "minLengthOfStay" : "maxLengthOfStay",
          messageArgs: difference <= minStay ? minStay : maxStay
        });
      }
    };
    $[19] = config.adults;
    $[20] = config.children;
    $[21] = rulesOfStay;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const onChange = t6;
  let t7;
  if ($[23] !== config || $[24] !== dates || $[25] !== onSubmit) {
    t7 = () => {
      const [from_0, to_0] = dates;
      onSubmit({
        ...config,
        from: from_0,
        to: to_0
      }, false);
    };
    $[23] = config;
    $[24] = dates;
    $[25] = onSubmit;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const handleSubmit = t7;
  let t8;
  if ($[27] !== config || $[28] !== onSubmit) {
    t8 = t9 => {
      const [from_1, to_1] = t9;
      onSubmit({
        ...config,
        from: from_1,
        to: to_1
      }, true);
    };
    $[27] = config;
    $[28] = onSubmit;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const handleSelectAlternativeDate = t8;
  let t9;
  if ($[30] !== handleSubmit || $[31] !== isDisabled || $[32] !== isFetching || $[33] !== isSubmitting) {
    t9 = () => <Modal.Footer className="border-t border-t-primary/15 sm:border-t-0"><Modal.Footer.Actions><Modal.Footer.Button as={LoadingButton} isLoading={isFetching || isSubmitting} onClick={handleSubmit} disabled={isDisabled}>Übernehmen</Modal.Footer.Button></Modal.Footer.Actions></Modal.Footer>;
    $[30] = handleSubmit;
    $[31] = isDisabled;
    $[32] = isFetching;
    $[33] = isSubmitting;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== dates || $[36] !== isDateUnavailable || $[37] !== isInvalid || $[38] !== max || $[39] !== min || $[40] !== onChange) {
    t10 = <div className="flex-1"><DateRangePicker value={dates} min={min} max={max} onChange={onChange} isInvalid={isInvalid} isDateUnavailable={isDateUnavailable} /></div>;
    $[35] = dates;
    $[36] = isDateUnavailable;
    $[37] = isInvalid;
    $[38] = max;
    $[39] = min;
    $[40] = onChange;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  const t11 = data?.rooms.length;
  let t12;
  if ($[42] !== accommodation || $[43] !== dates || $[44] !== isFetching || $[45] !== rulesOfStay || $[46] !== t11) {
    t12 = <ErrorMessages dates={dates} rulesOfStay={rulesOfStay} accommodation={accommodation} numberOfRooms={t11} isLoading={isFetching} />;
    $[42] = accommodation;
    $[43] = dates;
    $[44] = isFetching;
    $[45] = rulesOfStay;
    $[46] = t11;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  const t13 = data?.alternativeDates;
  let t14;
  if ($[48] !== dates || $[49] !== handleSelectAlternativeDate || $[50] !== t13) {
    t14 = <AppAlternativeDateChips dates={dates} alternativeDates={t13} onSelect={handleSelectAlternativeDate} />;
    $[48] = dates;
    $[49] = handleSelectAlternativeDate;
    $[50] = t13;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  let t15;
  if ($[52] !== t12 || $[53] !== t14) {
    t15 = <div className="flex min-h-40 flex-col gap-4 px-6 sm:px-0">{t12}{t14}</div>;
    $[52] = t12;
    $[53] = t14;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  let t16;
  if ($[55] !== t10 || $[56] !== t15) {
    t16 = <div className="flex h-full flex-col justify-between">{t10}{t15}</div>;
    $[55] = t10;
    $[56] = t15;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  let t17;
  if ($[58] !== t16 || $[59] !== t9) {
    t17 = <Modal.Content className="px-0 pt-4 pb-0" renderFooter={t9}>{t16}</Modal.Content>;
    $[58] = t16;
    $[59] = t9;
    $[60] = t17;
  } else {
    t17 = $[60];
  }
  return t17;
}
type ErrorMessagesProps = {
  dates: Dates;
  rulesOfStay: RulesOfStay;
  accommodation: Accommodation;
  numberOfRooms: number | undefined;
  isLoading: boolean;
};
function ErrorMessages(t0) {
  const $ = _c(21);
  const {
    dates,
    rulesOfStay,
    accommodation,
    numberOfRooms,
    isLoading
  } = t0;
  const [settledDates, setSettledDates] = useState(dates);
  if (!isLoading && dates[0] && dates[1] && dates !== settledDates) {
    setSettledDates(dates);
  }
  const [from, to] = settledDates;
  if (!from || !to) {
    return null;
  }
  let t1;
  if ($[0] !== from || $[1] !== rulesOfStay || $[2] !== to) {
    t1 = () => {
      const {
        min,
        max,
        difference
      } = getMinMaxLengthOfStay([from, to], rulesOfStay);
      if (difference < min) {
        return {
          type: "warning",
          message: min === 1 ? `Mindestaufenthalt: ${min} Nacht` : `Mindestaufenthalt: ${min} Nächte`
        };
      }
      if (difference > max) {
        return {
          type: "warning",
          message: `Maximalaufenthalt: ${max} Nächte`
        };
      }
      return null;
    };
    $[0] = from;
    $[1] = rulesOfStay;
    $[2] = to;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const getValidationMessage = t1;
  let t2;
  if ($[4] !== getValidationMessage) {
    t2 = getValidationMessage();
    $[4] = getValidationMessage;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const message = t2;
  const hasRooms = numberOfRooms !== undefined && numberOfRooms > 0;
  if (!message && hasRooms) {
    return null;
  }
  let t3;
  if ($[6] !== accommodation.lat || $[7] !== accommodation.lng || $[8] !== from || $[9] !== to) {
    const polygon = circleToPolygon({
      lat: accommodation.lat,
      lng: accommodation.lng
    }, 50);
    t3 = getMapUrl({
      polygon,
      from,
      to,
      productType: ProductType.ACCOMMODATION
    });
    $[6] = accommodation.lat;
    $[7] = accommodation.lng;
    $[8] = from;
    $[9] = to;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const mapUrl = t3;
  if (message) {
    let t4;
    if ($[11] !== mapUrl || $[12] !== message.actionLabel) {
      t4 = message.actionLabel ? {
        label: message.actionLabel,
        href: mapUrl,
        newTab: true
      } : undefined;
      $[11] = mapUrl;
      $[12] = message.actionLabel;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    let t5;
    if ($[14] !== message.message || $[15] !== message.type || $[16] !== t4) {
      t5 = <MessageBox type={message.type} action={t4}>{message.message}</MessageBox>;
      $[14] = message.message;
      $[15] = message.type;
      $[16] = t4;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    return t5;
  }
  let t4;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = shopTestId("DateModalContent.NoRoomsAvailable");
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== mapUrl) {
    t5 = <MessageBox testId={t4} type="error" action={{
      label: "Andere Unterk\xFCnfte anzeigen",
      href: mapUrl,
      newTab: true,
      onClick: _temp
    }}>Zum gewählten Zeitraum ist die Unterkunft leider nicht verfügbar.</MessageBox>;
    $[19] = mapUrl;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  return t5;
}
function _temp() {
  return trackClickCta("show other", {
    type: "accommodation"
  });
}